import React, { useEffect, useState } from 'react';
import styles from './access.module.css';
import { fontSize, palette } from '../..';
import { Tooltip, FormControl, Select, MenuItem } from '@mui/material';
import StepSlider from './Slider';
import { KeyedMutator } from 'swr';
import { TAlert } from '../../types/types';
import { useLocation, useParams } from 'react-router-dom';
import FetchData from '../ServerCall/FetchData';
import { NotificationDialog } from '../CommonFunctions/DialogComponents';

type UserInRow = {
  id: string;
  name: string;
  isGroup: boolean;
  emailId: string;
  role: 'owner' | 'moderator' | 'creator' | 'custom_creator' | 'player';
  customPermissions?: [
    {
      contentTypeId: number;
      privilegeId: number;
    },
  ];
};

const AccessForContentTable = ({
  contentTypes,
  currentContent,
  accessList,
  customPermissions,
  rowInEditMode,
  originalAccessList,
  addedUser,

  setShowUserDeleteConfirmation,
  setUser_GroupID,
  setUser_GroupName,
  setIsInGroup,
  setCustomPermissions,
  setRowInEditMode,
  mutateAccessList,
  setAddedUser,
}: {
  contentTypes: {
    id: number;
    value: string;
    displayValue: string;
  }[];
  rowInEditMode: UserInRow | null;
  currentContent: any;
  accessList: any[];
  originalAccessList: any[];
  customPermissions: any;
  addedUser: UserInRow | null;

  setShowUserDeleteConfirmation: React.Dispatch<React.SetStateAction<boolean>>;
  setUser_GroupID: React.Dispatch<React.SetStateAction<any>>;
  setUser_GroupName: React.Dispatch<React.SetStateAction<any>>;
  setIsInGroup: React.Dispatch<React.SetStateAction<any>>;
  setCustomPermissions: React.Dispatch<React.SetStateAction<any>>;
  setRowInEditMode: React.Dispatch<React.SetStateAction<UserInRow | null>>;
  setAddedUser: React.Dispatch<React.SetStateAction<UserInRow | null>>;

  mutateAccessList: KeyedMutator<any>;
}) => {
  const handleDelete = (userGroupId: any, userName: any, isGroup: any, extraParam: any) => {
    setShowUserDeleteConfirmation(true);
    setUser_GroupID(userGroupId);
    setUser_GroupName(userName);
    setIsInGroup(isGroup);
  };

  const contentId = useParams().parentId;
  const parentId = useLocation().state.parentId;

  const [alert, setAlert] = useState<null | TAlert>(null);

  const [hoverRowState, setHoverRowState] = useState<number | null>(null);

  useEffect(() => {
    if (alert) {
      setTimeout(() => {
        setAlert(null);
      }, 3000);
    }
  }, [alert]);

  let roles: any[] = [
    { id: 3, value: 'owner', displayValue: 'Owner' },
    { id: 4, value: 'moderator', displayValue: 'Moderator' },
    { id: 5, value: 'creator', displayValue: 'Creator' },
    { id: 7, value: 'player', displayValue: 'Player' },
    { id: 6, value: 'custom_creator', displayValue: 'Custom Creator' },
  ];

  const removeEditModeRow = () => {
    setRowInEditMode(null);
  };

  const sortAccessList = (accessList: any[]) => {
    return accessList.sort((a, b) => {
      if (a.id === addedUser?.id) return -1;
      if (b.id === addedUser?.id) return 1;
      return a.name.localeCompare(b.name);
    });
  };

  const handleEditRowButtonClick = (rowId: string) => {
    if (rowInEditMode && rowInEditMode.id === rowId) {
      setAlert({
        message: 'This row is already being edited.',
        severity: 'warning',
      });
      return;
    }

    if (rowInEditMode) {
      setAlert({
        message: 'There are unsaved changes. Can not proceed editing this row.',
        severity: 'error',
      });
      return;
    }

    setRowInEditMode({
      id: rowId,
      name: accessList.find((access) => access.id === rowId)?.name,
      isGroup: accessList.find((access) => access.id === rowId)?.isGroup,
      emailId: accessList.find((access) => access.id === rowId)?.emailId,
      role: accessList.find((access) => access.id === rowId)?.role,
      customPermissions: accessList.find((access) => access.id === rowId)?.customPermissions,
    });
  };

  const handleSaveButtonClick = async () => {
    if (!rowInEditMode) {
      setAlert({
        message: 'No row is being edited.',
        severity: 'error',
      });
      return;
    }
    //const roleId: any = roleMapping[selectedUser.role || selectedUser.roleName];

    const roleId = roles.find((item: any) => item.value === rowInEditMode?.role)?.id;

    let payload = [];

    // Set custom permissions if the role is custom_creator
    if (roleId === 6) {
      payload = [
        {
          granteeId: rowInEditMode?.id,
          isGroup: rowInEditMode?.isGroup,
          roleId: roleId,
          customPermissions: [
            {
              contentTypeId: currentContent.contentType,
              privilegeId:
                customPermissions[rowInEditMode?.id]?.[currentContent.contentType] || null,
              contentId: contentId,
            },
          ], //.filter((permission) => permission.privilegeId !== null),
        },
      ];
    } else {
      payload = [
        {
          granteeId: rowInEditMode?.id,
          isGroup: rowInEditMode?.isGroup,
          roleId: roleId,
        },
      ];
    }

    try {
      const userAccessChangeResponse = await FetchData({
        requestType: 'withData',
        method: 'POST',
        url: `workspace/user-access?workspaceId=${parentId}`,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          'Content-Type': 'application/json',
        },
        data: payload,
      });

      if (userAccessChangeResponse.status) {
        mutateAccessList();
        setAddedUser(null);
        removeEditModeRow();
        setAlert({
          message: 'Changes saved successfully',
          severity: 'success',
        });
      }
    } catch (error) {
      setAlert({
        message: 'Error saving changes. Try Again',
        severity: 'error',
      });
    }
  };

  const handlePermissionChange = (userGroupId: any, contentTypeId: any, privilegeId: any) => {
    // Update customPermissions
    setCustomPermissions((prev: any) => ({
      ...prev,
      [userGroupId]: {
        ...prev[userGroupId],
        [contentTypeId]:
          privilegeId !== 0 && privilegeId !== null ? Math.min(Math.max(privilegeId, 1), 3) : null, // Ensure privilegeId is between 1 and 3, or null
      },
    }));

    // Create a new accessList and update the respective user's customPermissions
    const updatedAccessList = accessList.map((access: any) => {
      if (access.id === userGroupId) {
        // Create or update the customPermissions for the current user
        const updatedPermissions = [
          {
            contentTypeId: currentContent.contentType,
            privilegeId: customPermissions[userGroupId]?.[currentContent.contentType] || null,
          },
        ].filter((permission) => permission.privilegeId !== null);
        return { ...access, customPermissions: updatedPermissions };
      }
      return access;
    });
    mutateAccessList(updatedAccessList, false);
  };

  const isCurrentRowInEditMode = (rowId: string) => rowInEditMode?.id === rowId;

  return (
    <>
      {alert && (
        <NotificationDialog
          onCloseAlert={() => setAlert(null)}
          severity={alert?.severity}
          testMessage={alert?.message}
          openAlert={!!alert}
        />
      )}
      <div
        style={{
          display: 'flex',
        }}
      >
        <table className={`${styles.resourcesTable} resourcesTable`}>
          <thead>
            <tr>
              <th
                style={{
                  color: palette.secondary.contrastText,
                  fontSize: fontSize.large,
                  backgroundColor: palette.primary.grey,
                }}
                rowSpan={3}
              >
                User/Group
              </th>
              <th
                style={{
                  color: palette.secondary.contrastText,
                  fontSize: fontSize.large,
                  backgroundColor: palette.primary.grey,
                }}
                rowSpan={3}
              >
                {contentTypes[currentContent.contentType - 1].displayValue} Role
              </th>
              <th
                style={{
                  color: palette.secondary.contrastText,
                  fontSize: fontSize.large,
                  backgroundColor: palette.primary.grey,
                }}
                colSpan={2}
              >
                Resources
              </th>
              <th
                style={{
                  color: palette.secondary.contrastText,
                  fontSize: fontSize.large,
                  backgroundColor: palette.primary.grey,
                }}
                rowSpan={3}
              >
                Actions
              </th>
            </tr>
            <tr>
              <th
                colSpan={2}
                style={{
                  color: palette.secondary.contrastText,
                  fontSize: fontSize.large,
                  backgroundColor: palette.primary.grey,
                }}
              >
                {contentTypes[currentContent.contentType - 1].displayValue}
              </th>
            </tr>
            <tr>
              <React.Fragment key={currentContent.contentType - 1}>
                <Tooltip title="View" arrow
                  slotProps={{
                    tooltip: {
                      sx: {
                        fontSize: '12px',
                      },
                    },
                  }}
                >
                  <th
                    style={{
                      color: palette.secondary.contrastText,
                      fontSize: fontSize.large,
                      backgroundColor: palette.primary.grey,
                    }}
                  >
                    <img
                      src="/view_header.png"
                      alt="View"
                      style={{ width: '17px', height: '17px' }}
                    />
                  </th>
                </Tooltip>
                <Tooltip title="Edit" arrow
                  slotProps={{
                    tooltip: {
                      sx: {
                        fontSize: '12px',
                      },
                    },
                  }}
                >
                  <th
                    style={{
                      color: palette.secondary.contrastText,
                      fontSize: fontSize.large,
                      backgroundColor: palette.primary.grey,
                    }}
                  >
                    <img
                      src="/edit_header.png"
                      alt="Edit"
                      style={{ width: '17px', height: '17px' }}
                    />
                  </th>
                </Tooltip>
              </React.Fragment>
            </tr>
          </thead>
          <tbody>
            {sortAccessList(accessList).map((access: any, rowIndex: number) => (
              <tr
                onMouseEnter={() => setHoverRowState(rowIndex)}
                onMouseLeave={() => setHoverRowState(null)}
                className={styles.tableRow}
                key={access.id}
                style={{
                  border: `${isCurrentRowInEditMode(access.id) ? '2px solid black' : 'none'}`,
                  fontWeight: `${isCurrentRowInEditMode(access.id) ? 'bold' : 'normal'}`,
                  backgroundColor: `${isCurrentRowInEditMode(access.id) ? '#f9f9f9' : ''}`,
                }}
              >
                <td
                  style={{
                    fontSize: fontSize.medium,
                    color: palette.primary.contrastText,
                    textAlign: 'start',
                    fontWeight: isCurrentRowInEditMode(access.id) ? 'bold' : 'normal',
                  }}
                >
                  {access.name}
                </td>
                <td
                  style={{
                    fontSize: fontSize.medium,
                    color: palette.primary.contrastText,
                    textAlign: 'center',
                    fontWeight: isCurrentRowInEditMode(access.id) ? 'bold' : 'normal',
                  }}
                >
                  {access.role === 'custom_creator' ? (
                    'Custom Creator'
                  ) : (
                    <FormControl
                      fullWidth
                      disabled={!isCurrentRowInEditMode(access.id)}
                      style={{
                        cursor: isCurrentRowInEditMode(access.id) ? 'pointer' : 'not-allowed',
                      }}
                      size="small"
                      sx={{
                        margin: 0,
                        textAlign: 'center',
                        width: '8.125rem',
                        fontWeight: 'inherit',
                      }}
                    >
                      <Select
                        value={access.id === rowInEditMode?.id ? rowInEditMode?.role : access.role}
                        onChange={(e) => {
                          if (rowInEditMode && rowInEditMode.id === access.id) {
                            setRowInEditMode({
                              ...rowInEditMode,
                              role: e.target.value as any,
                            });
                          }
                        }}
                        displayEmpty
                        sx={{
                          color: 'black',
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'black !important',
                          },
                          '& .MuiSelect-select': {
                            padding: '8.5px 5px !important',
                          },
                          '& .MuiOutlinedInput-notchedOutline': {
                            border: isCurrentRowInEditMode(access.id)
                              ? '2px solid black'
                              : '1px solid transparent',
                          },
                        }}
                      >
                        {roles.map((role, index) => (
                          <MenuItem
                            key={index}
                            value={role.value}
                            sx={{
                              '&.Mui-selected': {
                                backgroundColor: 'rgba(175, 153, 219, 0.3) !important',
                              },
                              '&.Mui-selected:hover': {
                                backgroundColor: 'rgba(175, 153, 219, 0.5) !important',
                              },
                            }}
                          >
                            {role.displayValue}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                </td>
                {Array(1)
                  .fill(null)
                  .map((_, index) => {
                    const privilegeId =
                      customPermissions[access.id]?.[currentContent.contentType] || 0;

                    return (
                      <React.Fragment key={access.id}>
                        <td colSpan={2} style={{}}>
                          <StepSlider
                            isEditActive={access.id === rowInEditMode?.id}
                            role={
                              access.id === rowInEditMode?.id ? rowInEditMode?.role : access.role
                            }
                            column={currentContent.contentType}
                            initialValue={privilegeId}
                            hideCreate={true}
                            onPermissionChange={(column: any, privilegeId: any) =>
                              handlePermissionChange(access.id, column, privilegeId)
                            }
                          />
                        </td>
                      </React.Fragment>
                    );
                  })}
                <td
                  className={styles.actButton}
                  style={{
                    textAlign: 'center',
                    fontWeight: isCurrentRowInEditMode(access.id) ? 'bold' : 'normal',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      gap: '4px',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <button
                      onClick={() => handleEditRowButtonClick(access.id)}
                      style={{
                        border: 'none',
                        background: 'none',
                      }}
                    >
                      <Tooltip title="Edit"
                        slotProps={{
                          tooltip: {
                            sx: {
                              fontSize: '12px',
                            },
                          },
                        }}
                      >
                        <div className={`${styles.accessEditIcon}`}></div>
                        {/* <img
                          src={hoverRowState === rowIndex ? '/edit.png' : '/edit_grey.png'}
                          alt="Edit Users Logo"
                          style={{ width: '17px', height: '17px' }}
                        /> */}
                      </Tooltip>
                    </button>
                    <button
                      className={styles.iconButton}
                      onClick={() =>
                        handleDelete(access.id, access.name, access.isGroup, access.roleName)
                      }
                    >
                      <Tooltip title="Delete"
                        slotProps={{
                          tooltip: {
                            sx: {
                              fontSize: '12px',
                            },
                          },
                        }}
                      >
                        <div className={`${styles.accessDeleteIcon}`}></div>
                        {/* <img
                          src={hoverRowState === rowIndex ? '/delete_red.png' : '/delete_grey.png'}
                          alt="Delete User"
                          style={{ width: '17px', height: '17px' }}
                        /> */}
                      </Tooltip>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div
          style={{
            paddingTop: '116px',
            marginLeft: '-12px',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {sortAccessList(accessList).map((access) => (
            <div
              key={access.id}
              style={{
                height: '52px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                gap: '4px',
                width: '80px',
                padding: '8px 4px',
                boxSizing: 'border-box',
              }}
            >
              <Tooltip title="Save Changes"
                slotProps={{
                  tooltip: {
                    sx: {
                      fontSize: '12px',
                    },
                  },
                }}
              >
                <button
                  onClick={() => handleSaveButtonClick()}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: rowInEditMode?.id === access.id ? 'pointer' : 'default',
                    padding: '6px',
                    opacity: rowInEditMode?.id === access.id ? 1 : 0,
                    pointerEvents: rowInEditMode?.id === access.id ? 'auto' : 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <img
                    style={{
                      width: '17px',
                      height: '17px',
                    }}
                    src="/green-tick.png"
                  />
                </button>
              </Tooltip>
              <Tooltip title="Cancel Changes"
                slotProps={{
                  tooltip: {
                    sx: {
                      fontSize: '12px',
                    },
                  },
                }}
              >
                <button
                  onClick={() => {
                    if (!rowInEditMode?.id) return;

                    // If the length of originalAccessList is same as AccessList that means a new row is added
                    // In this case we just remove the whole accessList so even if this person is still there we won't see them in table

                    if (originalAccessList.length !== accessList.length) {
                      setAddedUser(null);
                      mutateAccessList(
                        [...accessList].filter((access) => access.id !== rowInEditMode.id),
                        false,
                      );
                    } else {
                      const originalAccess = originalAccessList.find(
                        (access) => access.id === rowInEditMode.id,
                      );
                      if (originalAccess) {
                        setAddedUser(null);
                        mutateAccessList(
                          accessList.map((access) =>
                            access.id === rowInEditMode.id ? originalAccess : access,
                          ),
                          false,
                        );

                        setCustomPermissions((prev: any) => {
                          const newPermissions = { ...prev };
                          if (originalAccess.limit) {
                            newPermissions[rowInEditMode.id] = {};
                            newPermissions[rowInEditMode.id][currentContent.contentType] =
                              originalAccess.limit;
                          } else {
                            delete newPermissions[rowInEditMode.id];
                          }
                          return newPermissions;
                        });
                      }
                    }
                    removeEditModeRow();
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: rowInEditMode?.id === access.id ? 'pointer' : 'default',
                    padding: '6px',
                    opacity: rowInEditMode?.id === access.id ? 1 : 0,
                    pointerEvents: rowInEditMode?.id === access.id ? 'auto' : 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <img
                    style={{
                      width: '14px',
                      height: '14px',
                    }}
                    src="/close-in-access.png"
                  />
                </button>
              </Tooltip>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default AccessForContentTable;
